import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { CaretRightIcon, CheckCircleIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { surface } from '@/components/ui/surface';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSpaceLedger } from '@/hooks/queries/useSpaceLedger';
import { PREVIEW_ACTIONS_NOTICE, useSpaceActionsHidden, useSpacePerspective } from '@/hooks/useSpacePartnerPreview';
import { useSpaceSettlementDraft } from '@/hooks/useSpaceSettlementDraft';
import { homeSheetFocusTarget, isLedger, readFailed } from '@/lib/spaces/home';
import { RUNNING_LEDGER, memberPhoto, participantName, settlementBalanceDisplay, settlementPeriodLabel, settlementPlanNotice, transferKey } from '@/lib/spaces/settlement';
import { cn } from '@/lib/utils';
import type { SpaceDetail } from '@/types/spaces';
import type { SpaceLedger, SpaceLedgerMember, SpaceTransfer } from '@/types/space-settlements';
import { SettlementAvatar, SettlementFailure, SettlementMoney, SettlementTransferRow } from './SettlementParts';
import { SettlementHistory } from './SettlementHistory';
import { SpacePersonExpensesDrawer } from './SpacePersonExpensesDrawer';

const EYEBROW = 'font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary';

// The running balance: a debt left in a past month is still owed. Registering stores the request before sending it,
// a conflict asks for a fresh plan, and an uncertain send is re-sent exactly as it was, so a reload never turns an
// ambiguous success into a second payment.
export function SpaceSettlementSheet({ space, userId, close }: Readonly<{ space: SpaceDetail; userId: string; close: () => void }>) {
  const query = useSpaceLedger(space.id, RUNNING_LEDGER);
  // The plan being ticked is the one the sheet opened on: a background revalidation must not swap the transfers under
  // someone's checkboxes. A conflict fetches a fresh one on purpose (`refresh`).
  const [opened, setOpened] = useState<SpaceLedger | null>(null);
  const [busy, setBusy] = useState(false);
  const [returnTarget] = useState(() => document.activeElement);
  // The person whose expenses are open over the sheet; the sheet stays mounted, so its ticks survive the look.
  const [person, setPerson] = useState<SpaceLedgerMember | null>(null);
  const registered = useRef(false);
  const failed = readFailed(query, isLedger);
  if (!opened && !failed && query.data) setOpened(query.data);
  const handleClose = () => { if (!busy) close(); };
  const handleOpenChange = (open: boolean) => { if (!open) handleClose(); };
  const handleRegistered = () => { registered.current = true; close(); };
  // No Radix Trigger opened this sheet, so focus goes back by hand. A registration can remove the button that opened it
  // (the card's action changes), so after one the home's title takes focus.
  const handleRestoreFocus = (event: Event) => {
    event.preventDefault();
    homeSheetFocusTarget(registered.current ? null : returnTarget)?.focus();
  };
  const refresh = async () => {
    const updated = await query.mutate();
    if (!isLedger(updated)) throw new Error('Ledger unavailable');
    setOpened(updated);
    return updated;
  };
  const closePerson = () => setPerson(null);
  const body = opened
    ? <SettlementForm space={space} userId={userId} ledger={opened} onBusy={setBusy} onRegistered={handleRegistered} refresh={refresh} close={handleClose} onPerson={setPerson} />
    : <SheetState failed={failed} retry={query.mutate} />;
  return <>
    <ResponsiveDrawer open onOpenChange={handleOpenChange} dismissible={!busy}>
      <DrawerContent onCloseAutoFocus={handleRestoreFocus} className="overflow-clip bg-surface p-0 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-lg">
        <DrawerHeader className="shrink-0 border-b border-border-subtle px-5 py-4 text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
          <div className="flex items-center justify-between gap-3"><DrawerTitle>Acerto</DrawerTitle><Button variant="ghost" size="icon" aria-label="Fechar acerto" disabled={busy} onClick={handleClose}><XIcon aria-hidden className="size-4" /></Button></div>
          <DrawerDescription className="break-words text-text-secondary">{space.name} · {settlementPeriodLabel(opened?.periodKey ?? RUNNING_LEDGER)}</DrawerDescription>
        </DrawerHeader>
        {body}
      </DrawerContent>
    </ResponsiveDrawer>
    <PersonDrawer space={space} ledger={opened} person={person} close={closePerson} />
  </>;
}

function PersonDrawer({ space, ledger, person, close }: Readonly<{ space: SpaceDetail; ledger: SpaceLedger | null; person: SpaceLedgerMember | null; close: () => void }>) {
  if (!ledger || !person) return null;
  return <SpacePersonExpensesDrawer key={person.userId} space={space} ledger={ledger} member={person} close={close} />;
}

function SheetState({ failed, retry }: Readonly<{ failed: boolean; retry: () => unknown }>) {
  if (failed) return <div className="px-5 py-4"><SettlementFailure message="Não foi possível carregar o acerto. Confira seu acesso ao espaço e tente novamente." retry={retry} /></div>;
  return <div aria-busy="true" aria-label="Carregando acerto" className="space-y-3 px-5 py-4"><Skeleton className="h-16 rounded-xl" /><Skeleton className="h-16 rounded-xl" /><Skeleton className="h-32 rounded-xl" /></div>;
}

function SettlementForm({ space, userId, ledger, onBusy, onRegistered, refresh, close, onPerson }: Readonly<{
  space: SpaceDetail; userId: string; ledger: SpaceLedger; onBusy: (busy: boolean) => void; onRegistered: () => void; refresh: () => Promise<SpaceLedger>; close: () => void;
  onPerson: (member: SpaceLedgerMember) => void;
}>) {
  const { draft, dispatch, submit } = useSpaceSettlementDraft(space.id, ledger, userId, onRegistered);
  const formId = useId();
  const viewer = useSpacePerspective(userId);
  const busy = draft.phase === 'pending';
  useEffect(() => { onBusy(busy); }, [busy, onBusy]);
  const recovering = Boolean(draft.request);
  const canSubmit = space.myRole === 'editor' && (recovering || (!space.archivedAt && ledger.canRegister));
  const transfers = draft.request?.transfers ?? ledger.suggestedTransfers;
  const toggle = useCallback((key: string) => dispatch({ type: 'toggle', key }), [dispatch]);
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); if (canSubmit) void submit(); };
  const handleRefresh = async () => {
    try { const updated = await refresh(); dispatch({ type: 'reset', ledger: updated, userId }); }
    catch { dispatch({ type: 'failure', phase: 'conflict', error: 'Não foi possível atualizar o acerto. Tente novamente.' }); }
  };
  const renderMember = (member: SpaceLedgerMember) => <MemberLine key={member.userId} space={space} member={member} onOpen={onPerson} />;
  // Plain buttons inside a form submit it: the people's lines and the history (a receipt, a page, a retry) stay outside,
  // so the form holds only the plan, and the footer's button reaches it through `form`.
  return <div className="flex min-h-0 flex-1 flex-col" aria-busy={busy}>
    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-4" data-vaul-no-drag>
      <form id={formId} onSubmit={handleSubmit} className="space-y-6">
        <section aria-label="Quem paga quem" className="space-y-3">
          <h3 className={EYEBROW}>Quem paga quem</h3>
          <Transfers space={space} userId={viewer} ledger={ledger} transfers={transfers} editable={canSubmit} enabled={draft.phase === 'select'} selected={draft.selected} toggle={toggle} />
          <PlanNote space={space} visible={transfers.length > 0} editable={canSubmit} />
          <DrawerMessage message={draft.error} />
          <RefreshPlan visible={draft.phase === 'conflict'} refresh={handleRefresh} />
        </section>
      </form>
      <section aria-label="Como chegamos aqui" className="space-y-1">
        <h3 className={EYEBROW}>Como chegamos aqui</h3>
        <ul className="divide-y divide-border-subtle">{ledger.members.map(renderMember)}</ul>
      </section>
      <SettlementHistory space={space} />
    </div>
    <Footer form={formId} visible={canSubmit && transfers.length > 0} busy={busy} recovering={recovering} count={draft.selected.length} blocked={draft.phase === 'conflict'} close={close} />
  </div>;
}

// The app's checkbox (Radix) is a button, so the card's checked tint reads its data-state instead of :checked.
function Transfers({ space, userId, ledger, transfers, editable, enabled, selected, toggle }: Readonly<{
  space: SpaceDetail; userId: string; ledger: SpaceLedger; transfers: SpaceTransfer[]; editable: boolean; enabled: boolean; selected: string[]; toggle: (key: string) => void;
}>) {
  if (!transfers.length) return <Balanced ledger={ledger} />;
  const renderReadOnly = (transfer: SpaceTransfer) => <li key={transferKey(transfer)} className={cn(surface.mutedPanel, 'p-3')}><SettlementTransferRow transfer={transfer} space={space} userId={userId} /></li>;
  if (!editable) return <ul className="space-y-2">{transfers.map(renderReadOnly)}</ul>;
  const renderTransfer = (transfer: SpaceTransfer) => {
    const key = transferKey(transfer);
    const handleToggle = () => toggle(key);
    return <label key={key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-border-subtle p-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5 has-disabled:cursor-default">
      <Checkbox className="mt-1.5 cursor-pointer disabled:cursor-default" value={key} checked={selected.includes(key)} onCheckedChange={handleToggle} />
      <span className="min-w-0 flex-1"><SettlementTransferRow transfer={transfer} space={space} userId={userId} /></span>
    </label>;
  };
  return <fieldset disabled={!enabled} className="min-w-0 space-y-2"><legend className="sr-only">Transferências realizadas</legend>{transfers.map(renderTransfer)}</fieldset>;
}

function Balanced({ ledger }: Readonly<{ ledger: SpaceLedger }>) {
  if (!ledger.sharedAmountCents && !ledger.registeredTransfers.length) return <p className="text-sm text-text-secondary">Nenhuma despesa compartilhada ainda.</p>;
  return <p className="flex items-center gap-2 text-sm font-medium text-success-text"><CheckCircleIcon aria-hidden className="size-5" />Tudo quite.</p>;
}

function PlanNote({ space, visible, editable }: Readonly<{ space: SpaceDetail; visible: boolean; editable: boolean }>) {
  const previewing = useSpaceActionsHidden();
  if (!visible) return null;
  const note = previewing ? PREVIEW_ACTIONS_NOTICE : editable ? 'Marque o que já foi pago. Registrar não faz Pix nem movimenta dinheiro.' : settlementPlanNotice(space);
  return <p className="text-xs leading-relaxed text-text-secondary">{note}</p>;
}

// Four figures explain the balance; the whole line opens the expenses behind the first of them, what the person paid.
function MemberLine({ space, member, onOpen }: Readonly<{ space: SpaceDetail; member: SpaceLedgerMember; onOpen: (member: SpaceLedgerMember) => void }>) {
  const format = useFormatCurrency();
  const handleOpen = () => onOpen(member);
  const name = participantName(space, member.userId);
  const balance = settlementBalanceDisplay(member.balanceCents);
  const facts = [`pagou ${format(member.paidCents / 100)}`, `parte ${format(member.owedCents / 100)}`];
  if (member.sentCents) facts.push(`transferiu ${format(member.sentCents / 100)}`);
  if (member.receivedCents) facts.push(`recebeu ${format(member.receivedCents / 100)}`);
  return <li aria-label={`Acerto de ${name}`} className="relative flex items-center gap-3 py-2.5 transition-colors hover:bg-surface-sunken/60">
    <SettlementAvatar name={name} photoUrl={memberPhoto(space, member.userId)} className="size-7 text-2xs" />
    <div className="min-w-0 flex-1"><p className="break-words text-sm font-medium text-text-primary">{name}</p><p className="break-words font-mono text-2xs text-text-secondary tabular-nums">{facts.join(' · ')}</p></div>
    <p className="shrink-0 text-right"><span className="sr-only">{balance.label}: </span><SettlementMoney cents={member.balanceCents} signed className={`text-sm font-semibold ${balance.color}`} /></p>
    <CaretRightIcon aria-hidden weight="bold" className="size-4 shrink-0 text-text-tertiary" />
    {/* Last in the DOM so it paints over the avatar too: one click target, and the line's text stays readable. */}
    <button type="button" onClick={handleOpen} aria-label={`Ver as despesas de ${name}`}
      className="absolute inset-0 cursor-pointer rounded-lg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring" />
  </li>;
}

function Footer({ form, visible, busy, recovering, count, blocked, close }: Readonly<{ form: string; visible: boolean; busy: boolean; recovering: boolean; count: number; blocked: boolean; close: () => void }>) {
  if (!visible) return null;
  return <div className="flex shrink-0 flex-wrap gap-2 border-t border-border-subtle bg-surface px-5 py-4">
    <Button type="button" variant="ghost" disabled={busy} onClick={close}>{recovering ? 'Fechar' : 'Cancelar'}</Button>
    <Button type="submit" form={form} className="flex-1" disabled={busy || blocked || !count}>{registrationLabel(busy, recovering, count)}</Button>
  </div>;
}

function DrawerMessage({ message }: Readonly<{ message: string | null }>) {
  if (!message) return null;
  return <p role="alert" className="text-sm text-error-text">{message}</p>;
}

function registrationLabel(busy: boolean, recovering: boolean, count: number): string {
  if (busy) return 'Registrando…';
  if (recovering) return 'Verificar registro';
  return `Confirmar ${count} ${count === 1 ? 'acerto' : 'acertos'}`;
}

function RefreshPlan({ visible, refresh }: Readonly<{ visible: boolean; refresh: () => void }>) {
  if (!visible) return null;
  return <Button type="button" variant="outline" onClick={refresh}>Atualizar acerto</Button>;
}
